import React, { useCallback, useEffect, useState } from 'react';
import { Space, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { DownloadOutlined } from '@ant-design/icons';
import { Card } from '@app/components/common/Card/Card';
import { Button } from '@app/components/common/buttons/Button/Button';
import { Table } from '@app/components/common/Table/Table';
import { DayjsDatePicker } from '@app/components/common/pickers/DayjsDatePicker';
import { notificationController } from '@app/controllers/notificationController';
import { useMounted } from '@app/hooks/useMounted';
import { AppDate, Dates } from '@app/constants/Dates';
import { Pagination } from '@app/api/category.api';
import { saveBlob } from '@app/api/reportDownload';
import {
  UsageReportFilter,
  UsageReportFormat,
  UsageReportRow,
  exportUsageReport,
  getUsageReport,
} from '@app/api/aiUsageReport.api';
import {
  CostReportFormat,
  CostReportResponse,
  CostReportRow,
  exportCostReport,
  getCostReport,
} from '@app/api/aiCostReport.api';

type DateRange = [AppDate | null, AppDate | null] | null;

const initialPagination: Pagination = {
  current: 1,
  pageSize: 20,
  total: 0,
  defaultPageSize: 20,
  showSizeChanger: true,
  pageSizeOptions: ['20', '50', '100'],
};

/**
 * Kill-switch for the AI Cost & Coins section — HIDDEN, not removed.
 *
 * Off until the report has something true to say: the cost figures need
 * `OPENAI_PRICE_TABLE` configured (it has no default by design, so every cell
 * reads as unknown without it) and `GET ai-draw/admin/cost-report` has to be
 * deployed on the environment the CMS points at.
 *
 * Flipping this back to `true` is the entire restore — the fetch, the columns,
 * the exports and the card below all stay wired.
 *
 * TODO(ai-cost-report): turn on once the price table + OPENAI_ADMIN_KEY are set
 * on the target backend and the endpoint is live.
 */
const SHOW_COST_REPORT = false;

/** Report rows carry a full timestamp, so show date + time (shared `formatDate` is date-only). */
const formatDateTime = (value: string) => (value ? new Date(value).toLocaleString() : '');

/**
 * `Da Nang, VN (device)` — the place plus where it came from. A device-resolved
 * city is the user's; an IP-derived one is the carrier gateway's, routinely a
 * different province on a mobile network. An uncaptured location stays empty
 * rather than showing an orphan source.
 */
const formatLocation = (row: UsageReportRow): string => {
  const place = [row.city, row.country].filter((part): part is string => Boolean(part)).join(', ');
  if (!place) return '';
  return row.geoSource ? `${place} (${row.geoSource})` : place;
};

/**
 * Money, or an explicit "not known".
 *
 * Four decimals because a single call costs fractions of a cent and two would round most
 * real charges to `$0.00`. `null` renders as a dash, NEVER as a zero: unknown and free are
 * different facts, and only one of them is true here.
 */
const formatUsd = (value: number | null): React.ReactNode =>
  value === null || value === undefined ? (
    <Typography.Text type="secondary">—</Typography.Text>
  ) : (
    `$${value.toFixed(4)}`
  );

const toFilter = (range: DateRange): UsageReportFilter => {
  const from = range?.[0];
  const to = range?.[1];
  // The picker is date-granular — widen each end to the whole day, otherwise picking the
  // same day for both ends would ask the API for a zero-length window.
  return {
    from: from ? from.startOf('day').toISOString() : undefined,
    to: to ? to.endOf('day').toISOString() : undefined,
  };
};

/** Colour-codes a generation attempt so a failed call is visible at a glance. */
const UsageStatusTag: React.FC<{ status: string }> = ({ status }) => {
  const colours: Record<string, string> = {
    done: 'green',
    failed: 'red',
    processing: 'blue',
  };
  return <Tag color={colours[status]}>{status}</Tag>;
};

/**
 * One labelled figure in the cost summary strip.
 */
const Figure: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Space direction="vertical" size={0}>
    <Typography.Text type="secondary" style={{ fontSize: '0.75rem' }}>
      {label}
    </Typography.Text>
    <Typography.Text strong style={{ fontSize: '1.1rem' }}>
      {children}
    </Typography.Text>
  </Space>
);

/**
 * Which users generated tutorials, when, from where and on what device — and, below it,
 * what that cost.
 *
 * Two tables rather than one: the top table is the client's five columns, one row per
 * GENERATION, and its layout is fixed. The bottom one is one row per USER and answers a
 * different question. They share the page's date filter and nothing else.
 */
const AiUsageReportPage: React.FC = () => {
  const { isMounted } = useMounted();
  const [dateRange, setDateRange] = useState<DateRange>(null);
  // Which format is downloading, so only the button that was clicked spins.
  const [exporting, setExporting] = useState<UsageReportFormat | null>(null);
  const [exportingCost, setExportingCost] = useState<CostReportFormat | null>(null);
  const [tableData, setTableData] = useState<{
    data: UsageReportRow[];
    pagination: Pagination;
    loading: boolean;
  }>({ data: [], pagination: initialPagination, loading: false });
  const [cost, setCost] = useState<{ report: CostReportResponse | null; loading: boolean }>({
    report: null,
    loading: false,
  });

  const fetch = useCallback(
    (pagination: Pagination, range: DateRange) => {
      setTableData((prev) => ({ ...prev, loading: true }));
      getUsageReport({
        page: pagination.current,
        limit: pagination.pageSize,
        ...toFilter(range),
      })
        .then((res) => {
          if (!isMounted.current) {
            return;
          }
          setTableData({
            data: res.data ?? [],
            pagination: { ...pagination, total: res.total },
            loading: false,
          });
        })
        .catch(() => {
          // Error toast is already shown by the httpApi response interceptor.
          if (isMounted.current) {
            setTableData((prev) => ({ ...prev, loading: false }));
          }
        });
    },
    [isMounted],
  );

  /**
   * The cost table is loaded separately from the generation table: it is not paginated
   * (one row per user, not per generation) and a slow billing lookup must not hold up
   * the table above it.
   */
  const fetchCost = useCallback(
    (range: DateRange) => {
      setCost((prev) => ({ ...prev, loading: true }));
      getCostReport(toFilter(range))
        .then((report) => {
          if (isMounted.current) {
            setCost({ report, loading: false });
          }
        })
        .catch(() => {
          if (isMounted.current) {
            setCost((prev) => ({ ...prev, loading: false }));
          }
        });
    },
    [isMounted],
  );

  useEffect(() => {
    fetch(initialPagination, null);
    if (SHOW_COST_REPORT) {
      fetchCost(null);
    }
  }, [fetch, fetchCost]);

  const handleTableChange = (pagination: Pagination) => {
    fetch(pagination, dateRange);
  };

  const handleRangeChange = (range: DateRange) => {
    setDateRange(range);
    // Back to page 1: the old offset means nothing against a different result set.
    fetch({ ...tableData.pagination, current: 1 }, range);
    if (SHOW_COST_REPORT) {
      fetchCost(range);
    }
  };

  const handleExport = (format: UsageReportFormat) => () => {
    setExporting(format);
    // Exports whatever the table is currently filtered to, ignoring paging — the point of
    // the file is the full matching set, not the page on screen.
    exportUsageReport(toFilter(dateRange), format)
      .then(({ blob, filename, truncated }) => {
        saveBlob(blob, filename ?? `ai-usage-report-${Dates.format(Dates.getToday(), 'YYYY-MM-DD')}.${format}`);
        if (truncated) {
          // The server capped the export. Say so — a short file that looks complete is
          // worse than no file, because it would be analysed as if it were the whole set.
          notificationController.warning({
            message: 'Export truncated',
            description: 'The report hit the server row limit. Narrow the date range to export the rest.',
          });
        }
      })
      .catch(() => {
        // Error toast is already shown by the httpApi response interceptor.
      })
      .finally(() => {
        if (isMounted.current) {
          setExporting(null);
        }
      });
  };

  const handleExportCost = (format: CostReportFormat) => () => {
    setExportingCost(format);
    exportCostReport(toFilter(dateRange), format)
      .then(({ blob, filename }) => {
        saveBlob(blob, filename ?? `ai-cost-report-${Dates.format(Dates.getToday(), 'YYYY-MM-DD')}.${format}`);
      })
      .catch(() => {
        // Error toast is already shown by the httpApi response interceptor.
      })
      .finally(() => {
        if (isMounted.current) {
          setExportingCost(null);
        }
      });
  };

  const columns: ColumnsType<UsageReportRow> = [
    {
      title: 'Date & Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_, record) => formatDateTime(record.createdAt),
    },
    {
      title: 'User Name',
      dataIndex: 'userName',
      key: 'userName',
      render: (_, record) => record.userName || '',
    },
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => formatLocation(record),
    },
    {
      title: 'Device Model',
      dataIndex: 'deviceModel',
      key: 'deviceModel',
      render: (_, record) => record.deviceModel || '',
    },
    {
      // Appended after the five columns the client asked for, so their
      // layout is untouched. A row is one generation ATTEMPT; without this
      // the reader cannot tell a finished drawing from a failed call that
      // still cost money.
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => <UsageStatusTag status={record.status} />,
    },
  ];

  const costColumns: ColumnsType<CostReportRow> = [
    {
      title: 'User Name',
      dataIndex: 'userName',
      key: 'userName',
      render: (_, record) => record.userName || '',
    },
    { title: 'User ID', dataIndex: 'userId', key: 'userId' },
    { title: 'Generations', dataIndex: 'generations', key: 'generations' },
    {
      // Retries included: a retried call is spent money even though it produced
      // no extra drawing, so counting only generations would under-report.
      title: 'AI Calls',
      dataIndex: 'calls',
      key: 'calls',
    },
    {
      title: 'AI Cost (USD)',
      key: 'costUsd',
      render: (_, record) => formatUsd(record.costUsd),
    },
    {
      // The caveat on the column to its left: how many calls carried no price
      // at all and are therefore missing from that figure.
      title: 'Unpriced Calls',
      dataIndex: 'unknownCalls',
      key: 'unknownCalls',
      render: (_, record) =>
        record.unknownCalls > 0 ? <Tag color="orange">{record.unknownCalls}</Tag> : record.unknownCalls,
    },
    { title: 'Coins Spent', dataIndex: 'coinsSpent', key: 'coinsSpent' },
    { title: 'Coins Topped Up', dataIndex: 'coinsToppedUp', key: 'coinsToppedUp' },
  ];

  const org = cost.report?.org;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        padding="1.25rem 1.875rem"
        title="AI Usage Report"
        extra={
          // Both formats the client asked for. Excel leads because that is what
          // they named first and what a person actually opens; CSV stays for
          // whatever ingests the file rather than reads it.
          <Space size="small">
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={exporting === 'xlsx'}
              disabled={exporting !== null}
              onClick={handleExport('xlsx')}
            >
              Export Excel
            </Button>
            <Button
              icon={<DownloadOutlined />}
              loading={exporting === 'csv'}
              disabled={exporting !== null}
              onClick={handleExport('csv')}
            >
              Export CSV
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <DayjsDatePicker.RangePicker value={dateRange} onChange={handleRangeChange} allowClear />
          <Table
            loading={tableData.loading}
            columns={columns}
            dataSource={tableData.data}
            pagination={{ ...tableData.pagination, style: { alignItems: 'center' } }}
            onChange={handleTableChange}
            scroll={{ x: 800 }}
            bordered
            rowKey={(record) => record.id}
          />
        </Space>
      </Card>

      {/* Hidden behind SHOW_COST_REPORT — see the flag at the top of this file. */}
      {SHOW_COST_REPORT && (
        <Card
          padding="1.25rem 1.875rem"
          title="AI Cost & Coins"
          extra={
            <Space size="small">
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={exportingCost === 'xlsx'}
                disabled={exportingCost !== null}
                onClick={handleExportCost('xlsx')}
              >
                Export Excel
              </Button>
              <Button
                icon={<DownloadOutlined />}
                loading={exportingCost === 'csv'}
                disabled={exportingCost !== null}
                onClick={handleExportCost('csv')}
              >
                Export CSV
              </Button>
            </Space>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space size="large" wrap>
              <Figure label="Our recorded ai-draw cost">{formatUsd(cost.report?.totals.costUsd ?? null)}</Figure>
              <Figure label="OpenAI organisation spend (whole org)">
                {org?.available ? (
                  <>
                    {formatUsd(org.spendUsd)}
                    {org.partial && (
                      <Tag color="orange" style={{ marginLeft: 8 }}>
                        partial
                      </Tag>
                    )}
                  </>
                ) : (
                  // Said out loud rather than shown as a zero or hidden: an admin
                  // must be able to tell "nothing was spent" from "we could not ask".
                  <Typography.Text type="secondary" style={{ fontSize: '0.9rem', fontWeight: 400 }}>
                    unavailable — {org?.reason ?? 'not loaded'}
                  </Typography.Text>
                )}
              </Figure>
              <Figure label="Generations">{cost.report?.totals.generations ?? 0}</Figure>
              <Figure label="Coins spent / topped up">
                {(cost.report?.totals.coinsSpent ?? 0).toLocaleString()} /{' '}
                {(cost.report?.totals.coinsToppedUp ?? 0).toLocaleString()}
              </Figure>
            </Space>

            {(cost.report?.totals.unknownCalls ?? 0) > 0 && (
              <Typography.Text type="secondary">
                {cost.report?.totals.unknownCalls} AI call(s) in this range carry no price and are missing from the cost
                above. That happens when a model returns no usage payload, or when it is not in OPENAI_PRICE_TABLE.
              </Typography.Text>
            )}

            <Table
              loading={cost.loading}
              columns={costColumns}
              dataSource={cost.report?.rows ?? []}
              pagination={{ pageSize: 20, style: { alignItems: 'center' } }}
              scroll={{ x: 900 }}
              bordered
              rowKey={(record) => record.userId}
            />
          </Space>
        </Card>
      )}
    </Space>
  );
};

export default AiUsageReportPage;
